import React, { useEffect, useRef, useState } from 'react';
import './TulipChatbot.css';

// ========================================
// GROQ CONFIG
// ========================================
const API_KEY = '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';
const API_URL = 'https://api.groq.com/openai/v1/chat/completions';
const MODEL = 'llama-3.1-8b-instant';

// ========================================
// SYSTEM PROMPT
// ========================================
const SYSTEM_INSTRUCTION = `
Bạn là Tulina — trợ lý AI cực kỳ dễ thương của tiệm hoa Floraison 🌷

Nhiệm vụ:
- Tư vấn hoa Tulip, Hồng, Hướng Dương
- Gợi ý bó hoa theo dịp
- Trả lời tự nhiên như người thật
- Dùng emoji nhẹ nhàng 🌷✨💐

Thông tin:
- Miễn phí ship nội thành cho đơn trên 500k
- Có giao nhanh trong ngày
- Có thể tư vấn hoa sinh nhật, khai trương, tỏ tình, xin lỗi

Phong cách:
- Xưng "mình"
- Gọi khách là "bạn"
- Trả lời ngắn gọn
- Tự nhiên
- Dễ thương
`;

// ========================================
// COMPONENT
// ========================================
const TulipChatbot = () => {
  // ========================================
  // STATES
  // ========================================
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      text: 'Chào bạn! Mình là Tulina đây. Bạn cần mình tư vấn bó hoa nào không? 🌷',
      sender: 'bot',
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);

  // ========================================
  // AUTO SCROLL
  // ========================================
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen, isTyping]);

  // ========================================
  // SEND MESSAGE
  // ========================================
  const handleSend = async () => {
    if (!input.trim()) return;
    if (isTyping) return;

    const currentInput = input.trim();

    // USER MESSAGE
    const userMessage = {
      text: currentInput,
      sender: 'user',
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      // ========================================
      // BUILD CHAT HISTORY
      // ========================================
      const chatHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));

      // ========================================
      // API CALL
      // ========================================
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${API_KEY}`,
        },
        body: JSON.stringify({
          model: MODEL,
          messages: [
            { role: 'system', content: SYSTEM_INSTRUCTION },
            ...chatHistory,
            { role: 'user', content: currentInput }
          ],
          temperature: 0.9,
          max_tokens: 300,
          top_p: 1,
          stream: false
        })
      });

      // ========================================
      // RESPONSE
      // ========================================
      const data = await response.json();

      // ========================================
      // ERROR
      // ========================================
      if (!response.ok) {
        console.error(data);
        throw new Error(
          data?.error?.message || 'Không kết nối được AI'
        );
      }

      // ========================================
      // GET BOT RESPONSE
      // ========================================
      const botText = data?.choices?.[0]?.message?.content;
      if (!botText) {
        throw new Error('AI không phản hồi');
      }

      // ========================================
      // ADD BOT MESSAGE
      // ========================================
      setMessages(prev => [
        ...prev,
        {
          text: botText,
          sender: 'bot',
        }
      ]);
    } catch (error) {
      console.error('Lỗi:', error);
      setMessages(prev => [
        ...prev,
        {
          text: `🥀 Tulina đang bận. Bạn hãy thử nhắn lại nhé!`,
          sender: 'bot',
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  // ========================================
  // ENTER SEND
  // ========================================
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  // ========================================
  // TOGGLE CHAT
  // ========================================
  const handleToggle = () => {
    setIsOpen(prev => !prev);
  };

  // ========================================
  // UI
  // ========================================
  return (
    <div className="tulip-chatbot-container">
      {/* ========================================
          TRIGGER BUTTON
      ======================================== */}
      <div 
        className="tulip-trigger"
        onClick={handleToggle}
      >
        <svg 
          width="115" height="145" 
          viewBox="0 0 115 145" 
          fill="none"
          className="tulip-svg-animated"
        >
          <ellipse cx="57" cy="132" rx="22" ry="6" fill="rgba(0,0,0,0.12)" />
          
          <path d="M52 104 C34 100 22 92 18 82 C24 76 36 80 46 92 C48 96 50 100 52 104Z" fill="#63B86D" stroke="#000" strokeWidth="3" strokeLinejoin="round" />
          <path d="M62 104 C80 100 92 92 96 82 C90 76 78 80 68 92 C66 96 64 100 62 104Z" fill="#63B86D" stroke="#000" strokeWidth="3" strokeLinejoin="round" />
          
          <rect x="53" y="84" width="8" height="34" rx="4" fill="#4EA85B" stroke="#000" strokeWidth="3" />
          
          <path d="M30 48C29 30 41 17 51 23C48 39 49 54 53 69C43 71 33 65 30 48Z" fill="#FF8DA6" stroke="#000" strokeWidth="3" strokeLinejoin="round" />
          <path d="M85 48C86 30 74 17 64 23C67 39 66 54 62 69C72 71 82 65 85 48Z" fill="#FF8DA6" stroke="#000" strokeWidth="3" strokeLinejoin="round" />
          
          <path d="M57 22 C62 10 77 12 82 24 C95 20 102 32 98 46 C95 66 82 84 57 84 C32 84 19 66 16 46 C12 32 19 20 32 24 C37 12 52 10 57 22Z" fill="#FF6F91" stroke="#000" strokeWidth="3.5" strokeLinejoin="round" />
          <path d="M57 22C54 37 54 56 57 82" stroke="#FFA9BB" strokeWidth="5" strokeLinecap="round" opacity="0.9" />
          <path d="M38 29C40 22 47 20 51 24C47 34 47 45 49 56C42 53 36 42 38 29Z" fill="#FFD5DE" opacity="0.85" />
          
          <g className="tulip-eye-blink">
            <ellipse cx="44" cy="49" rx="8" ry="10" fill="#000" />
            <ellipse cx="70" cy="49" rx="8" ry="10" fill="#000" />
            <circle cx="47" cy="45" r="3.2" fill="white" />
            <circle cx="73" cy="45" r="3.2" fill="white" />
            <circle cx="42" cy="53" r="1.5" fill="white" />
            <circle cx="68" cy="53" r="1.5" fill="white" />
          </g>
          
          <path d="M37 37L34 33" stroke="#000" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M44 35L43 31" stroke="#000" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M77 37L80 33" stroke="#000" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M70 35L71 31" stroke="#000" strokeWidth="2.2" strokeLinecap="round" />
          
          <ellipse cx="30" cy="60" rx="6" ry="4" fill="#FFADC0" fillOpacity="0.55" />
          <ellipse cx="84" cy="60" rx="6" ry="4" fill="#FFADC0" fillOpacity="0.55" />
          <path d="M49 63C52 67 62 67 65 63" stroke="#000" strokeWidth="2.8" strokeLinecap="round" />
          <path d="M54 65C55 67 59 67 60 65" stroke="#FF7B94" strokeWidth="2" strokeLinecap="round" />
          
          <path d="M22 24L24 30L30 32L24 34L22 40L20 34L14 32L20 30L22 24Z" fill="#FFF1A8" stroke="#000" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      </div>

      {/* ========================================
          CHAT WINDOW
      ======================================== */}
      <div 
        className="tulip-window custom-chat-box" 
        style={{ display: isOpen ? 'flex' : 'none' }}
      >
        {/* HEADER */}
        <div className="chat-header">
          Trợ lý ảo Tulina 🌷
        </div>

        {/* MESSAGES */}
        <div 
          className="chat-messages"
          ref={scrollRef}
        >
          {messages.map((msg, idx) => (
            <div 
              key={idx} 
              className={`chat-bubble ${msg.sender}`}
            >
              {msg.text}
            </div>
          ))}
          {isTyping && (
            <div className="chat-bubble bot typing">
              Tulina đang gõ...
            </div>
          )}
        </div>

        {/* INPUT */}
        <div className="chat-input-area">
          <input 
            type="text" 
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Hỏi Tulina đi..."
            disabled={isTyping}
          />
          <button 
            onClick={handleSend}
            disabled={isTyping}
          >
            {isTyping ? '...' : '➤'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TulipChatbot;